import { Box, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { neutral } from '@ahora-patagonia/design-tokens';
import type { NotaPublicaCard } from '@ahora-patagonia/contracts';
import { formatearFecha } from '../../../shared/formato/fecha.js';
import { ImagenNota } from '../../../shared/media/ImagenNota.js';
import { SIZES } from '../../../shared/media/srcset.js';
import { VolantaCategoria } from '../../../shared/notas/VolantaCategoria.js';

/**
 * Las celdas de la portada: una por jerarquía, compartidas por las tres maquetas.
 *
 * Están acá y no en cada maqueta porque son **la misma nota vista más grande o más chica**, no
 * plantillas distintas: la chica de C es exactamente la que cierra A, y separarlas dejaría dos
 * copias que se van a desincronizar en el primer retoque tipográfico.
 */

/** La firma: autor y fecha, igual en todas las celdas. */
function Firma({ nota, mt = 1 }: { nota: NotaPublicaCard; mt?: number }) {
  return (
    <Typography variant="firma" component="p" sx={{ color: neutral[700], mt }}>
      {nota.author} · {formatearFecha(nota.publishedAt)}
    </Typography>
  );
}

/**
 * La posición 1 de la maqueta A: foto 16:9 **a todo el ancho de la celda** con
 * `fetchpriority="high"` (la única de la página) y el titular debajo, **entero, sin recorte y sin
 * bajar de tamaño** — es la nota principal del día; un titular de 90 caracteres a 27 px en 390
 * ocupa 4 líneas y se muestra completo. Bajada de 2 líneas (3 en escritorio).
 *
 * **Una sola forma, siempre**, venga la portada apaisada o vertical: la vertical entra por su
 * recorte 16:9, el que genera el worker encuadrado al tercio superior. Antes había una plantilla
 * alternativa para verticales —foto 3:4 al costado al 44 % del ancho, titular a la derecha— y el
 * resultado era peor que el recorte: la foto quedaba chica, el titular rompía en siete líneas
 * contra media columna y la celda terminaba 250 px más arriba que las posiciones 2 y 3, con un
 * hueco blanco abajo. La portada es la cara del diario: no cambia de forma según qué foto tocó.
 */
export function NotaPrincipal({ nota }: { nota: NotaPublicaCard }) {
  return (
    <Box component="article">
      <MuiLink component={RouterLink} to={nota.path} sx={{ display: 'block' }}>
        <ImagenNota imagen={nota.cover} ratio="16:9" sizes={SIZES.portada1} prioritaria />
      </MuiLink>
      <Box sx={{ pt: 1 }}>
        <VolantaCategoria nombre={nota.categories[0]?.name} />
        <MuiLink component={RouterLink} to={nota.path}>
          <Typography variant="titularPortada1" component="h2">
            {nota.title}
          </Typography>
        </MuiLink>
        {nota.excerpt && (
          <Typography
            variant="bajada"
            component="p"
            sx={{
              mt: 1,
              display: '-webkit-box',
              WebkitLineClamp: { xs: 2, lg: 3 },
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {nota.excerpt}
          </Typography>
        )}
        <Firma nota={nota} />
      </Box>
    </Box>
  );
}

/**
 * La nota única de la maqueta B: toma el frente entero del diario y no comparte la fila con nada.
 *
 * La foto se **achata** al subir de tamaño —de 8:5 en teléfono a 21:9 en escritorio—: a ancho
 * completo, un 16:9 se comería la pantalla entera y el titular quedaría bajo el pliegue. Ese 21:9
 * no existe como recorte guardado, así que se pide el 16:9 y la caja lo cierra por CSS (ver
 * `ImagenNota`). Es también la razón por la que B pide foto horizontal: una vertical entra, pero
 * recortada al centro.
 *
 * El titular va entero, sin recorte: a 62 px es lo primero y casi lo único que se lee.
 */
export function NotaUnicaB({ nota }: { nota: NotaPublicaCard }) {
  return (
    <Box component="article">
      <MuiLink component={RouterLink} to={nota.path} sx={{ display: 'block' }}>
        <ImagenNota
          imagen={nota.cover}
          ratio="16:9"
          caja={{ xs: '1.61 / 1', sm: '1.92 / 1', lg: '21 / 9' }}
          sizes={SIZES.portadaB}
          prioritaria
        />
      </MuiLink>
      <Box sx={{ pt: 1 }}>
        <VolantaCategoria nombre={nota.categories[0]?.name} />
        <MuiLink component={RouterLink} to={nota.path}>
          <Typography variant="titularPortadaB" component="h2">
            {nota.title}
          </Typography>
        </MuiLink>
        {nota.excerpt && (
          <Typography
            variant="bajada"
            component="p"
            sx={{
              mt: 1,
              // La columna de lectura se mide en caracteres, no en píxeles: a ancho completo una
              // bajada de borde a borde son 140 caracteres por línea y no se lee.
              maxWidth: { lg: '62ch' },
              // Es la bajada más grande del sitio, a la altura del titular que la encabeza.
              fontSize: { lg: 21 },
              display: '-webkit-box',
              WebkitLineClamp: { xs: 2, lg: 3 },
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {nota.excerpt}
          </Typography>
        )}
        <Firma nota={nota} />
      </Box>
    </Box>
  );
}

/**
 * Una de las dos notas del frente de la maqueta C.
 *
 * Las dos son **exactamente del mismo peso**: misma proporción de foto, mismo cuerpo de titular,
 * las dos con bajada, y ninguna lleva regla superior —así las dos fotos arrancan a la misma altura.
 * Que sean iguales es el punto de la maqueta: son dos notas que se disputan el frente, no una
 * principal y su acompañante.
 *
 * El titular recorta a tres líneas. Es la contracara de esa igualdad: las celdas comparten alto, y
 * sin tope un titular desbocado estiraría las dos. El recorte alcanza y se resuelve solo: no hay
 * aviso ni límite en el panel, porque un titular es una decisión editorial y el CMS no es quién
 * para discutirla.
 */
export function NotaFrenteC({ nota, prioritaria }: { nota: NotaPublicaCard; prioritaria: boolean }) {
  return (
    <Box component="article">
      <MuiLink component={RouterLink} to={nota.path} sx={{ display: 'block' }}>
        <ImagenNota
          imagen={nota.cover}
          ratio="16:9"
          sizes={SIZES.portadaC}
          {...(prioritaria ? { prioritaria: true } : {})}
        />
      </MuiLink>
      <Box sx={{ pt: 1 }}>
        <VolantaCategoria nombre={nota.categories[0]?.name} />
        <MuiLink component={RouterLink} to={nota.path}>
          <Typography
            variant="titularPortadaC"
            component="h2"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {nota.title}
          </Typography>
        </MuiLink>
        {nota.excerpt && (
          <Typography
            variant="bajada"
            component="p"
            sx={{
              mt: 1,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {nota.excerpt}
          </Typography>
        )}
        <Firma nota={nota} />
      </Box>
    </Box>
  );
}

/**
 * Posiciones 2-6 de A y las tres chicas de C: miniatura 4:3 (los recortes 16:9 son solo de la
 * posición 1 y las tiras). En teléfono son filas con la foto a la izquierda; desde tablet, tarjetas
 * con la foto arriba. El mismo DOM cambia de forma por CSS: nada se mide ni se re-renderiza.
 *
 * `regla` la pide la fila de tres de C, que va separada del frente por una línea fina y su sangría.
 */
export function NotaSecundaria({
  nota,
  grande = false,
  regla = false,
}: {
  nota: NotaPublicaCard;
  grande?: boolean;
  regla?: boolean;
}) {
  return (
    <Box
      component="article"
      sx={{
        display: 'flex',
        flexDirection: { xs: 'row', sm: 'column' },
        gap: { xs: 1.5, sm: 1 },
        alignItems: { xs: 'flex-start', sm: 'stretch' },
        ...(regla ? { borderTop: `1px solid ${neutral[300]}`, pt: 1.5 } : {}),
      }}
    >
      <MuiLink
        component={RouterLink}
        to={nota.path}
        sx={{ flexShrink: 0, width: { xs: 96, sm: '100%' }, display: 'block' }}
      >
        <ImagenNota imagen={nota.cover} ratio="4:3" sizes={SIZES.tarjeta} />
      </MuiLink>
      <Box sx={{ minWidth: 0 }}>
        <VolantaCategoria nombre={nota.categories[0]?.name} />
        <MuiLink component={RouterLink} to={nota.path}>
          <Typography
            variant={grande ? 'titularPortada23' : 'titularTarjeta'}
            component="h3"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {nota.title}
          </Typography>
        </MuiLink>
        <Firma nota={nota} mt={0.5} />
      </Box>
    </Box>
  );
}
